// Client-side image prep for the drop zone: resize to the house maximum,
// re-encode, read the size, and a soft check that the corners look like our
// parchment ground.

export const MAX_PX = 1600;
export const JPEG_QUALITY = 0.82;

export type Prepared = {
  blob: Blob;
  width: number;
  height: number;
  bytes: number;
  contentType: 'image/jpeg' | 'image/png';
  /** true when at least one corner is warm parchment; false = "not our usual look". */
  parchment: boolean;
  previewUrl: string;
};

function contentTypeFor(filename: string): 'image/jpeg' | 'image/png' {
  return /\.png$/i.test(filename) ? 'image/png' : 'image/jpeg';
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file could not be read as an image.'));
    };
    img.src = url;
  });
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('The image could not be encoded.'))), type, quality);
  });
}

/**
 * Four corners, 6 px in from each edge. A corner is warm when red leads blue
 * by 12 or more and it is not blown-out white (brightness at most 249).
 */
export function cornersLookLikeParchment(ctx: CanvasRenderingContext2D, w: number, h: number): boolean {
  const inset = Math.min(6, Math.floor(Math.min(w, h) / 4));
  const points: Array<[number, number]> = [
    [inset, inset],
    [w - 1 - inset, inset],
    [inset, h - 1 - inset],
    [w - 1 - inset, h - 1 - inset],
  ];
  for (const [x, y] of points) {
    const [r, g, b] = ctx.getImageData(x, y, 1, 1).data;
    const brightness = (r + g + b) / 3;
    if (r - b >= 12 && brightness <= 249) return true;
  }
  return false;
}

/** Resize to at most MAX_PX on the long side and re-encode for the bucket. */
export async function prepareImage(file: File, filename: string): Promise<Prepared> {
  const img = await loadImage(file);
  const scale = Math.min(1, MAX_PX / Math.max(img.naturalWidth, img.naturalHeight));
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('This browser cannot resize images.');
  // JPEG has no alpha: paint parchment behind a transparent PNG so the edges
  // do not turn black.
  ctx.fillStyle = '#F5EFE6';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  const contentType = contentTypeFor(filename);
  const blob = contentType === 'image/png' ? await toBlob(canvas, 'image/png') : await toBlob(canvas, 'image/jpeg', JPEG_QUALITY);
  return {
    blob,
    width,
    height,
    bytes: blob.size,
    contentType,
    parchment: cornersLookLikeParchment(ctx, width, height),
    previewUrl: URL.createObjectURL(blob),
  };
}

export function formatBytes(n: number | null | undefined): string {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
